"use client";

import { useId } from "react";
import { useTalentSearchFilters } from "@/components/search/talent-search-filters-provider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Icon } from "@/components/ui/icon";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { showToast } from "@/components/ui/sonner";
import { PoolIcon } from "@/features/talent-pools/components/pool-icon";
import {
  MAX_POOLS_PER_SEARCH,
  categoryLabel,
  clearCategories,
  joinNames,
  normalizePoolIds,
  unavailableCategories,
} from "@/features/talent-pools/lib/filter-categories";
import { useListTalentPools } from "@/lib/api/generated";
import type { TalentPoolResponse } from "@/lib/api/model";
import { cn } from "@/lib/utils";

/** Trigger shows at most this many pool icons. */
const TRIGGER_ICONS = 3;

/**
 * "Search in": which talent pools the draft searches (`pool_ids`). A search
 * covers one Arbi pool (radio) or 1..10 of the org's own pools (checkboxes),
 * never both: picking one kind replaces the other. Renders nothing outside a
 * search session (no `setPoolIds`). Changing the selection clears every
 * filter the new pools don't support and says so.
 */
export function SearchPoolsPicker({
  align = "end",
  className,
}: {
  align?: "start" | "end";
  className?: string;
}) {
  const { poolIds, setPoolIds, filters, setFilters } = useTalentSearchFilters();
  const radioId = useId();
  const { data, isLoading, isError } = useListTalentPools({
    query: { enabled: !!setPoolIds },
  });
  if (!setPoolIds) return null;

  // Standard first, then other global pools, then the org's own pools.
  const pools = [...(data?.items ?? [])].sort(
    (a, b) =>
      Number(b.is_standard) - Number(a.is_standard) ||
      Number(a.kind === "tenant") - Number(b.kind === "tenant") ||
      a.name.localeCompare(b.name),
  );
  const selected = new Set(poolIds ?? []);
  const selectedPools = pools.filter((p) => selected.has(p.id));
  const label = selectedPools.length
    ? selectedPools.length > 1
      ? `${selectedPools[0].name} +${selectedPools.length - 1}`
      : selectedPools[0].name
    : "Search in";
  const globalPools = pools.filter((p) => p.kind !== "tenant");
  const tenantPools = pools.filter((p) => p.kind === "tenant");
  const selectedGlobal = globalPools.find((p) => selected.has(p.id));
  const selectedTenants = tenantPools.filter((p) => selected.has(p.id));
  // A stored selection that still mixes kinds counts as global: any tenant
  // click then starts a tenant-only scope.
  const tenantScope = !selectedGlobal && selectedTenants.length > 0;

  const select = (ids: string[]) => {
    if (!data) return;
    const next = normalizePoolIds(ids);
    const gate = unavailableCategories(data.items, next);
    const { filters: cleared, cleared: categories } = clearCategories(filters, gate.keys());
    setPoolIds(next);
    if (categories.length === 0) return;
    setFilters(cleared);
    const blocking = [...new Set(categories.flatMap((c) => gate.get(c)!.map((p) => p.name)))];
    showToast.info(`Cleared ${joinNames(categories.map(categoryLabel))} filters`, {
      description: `Not available in ${joinNames(blocking)}.`,
    });
  };

  const toggleTenant = (id: string, checked: boolean) => {
    const current = tenantScope ? selectedTenants.map((p) => p.id) : [];
    select(checked ? [...current, id] : current.filter((p) => p !== id));
  };

  const rowBody = (pool: TalentPoolResponse) => (
    <>
      <PoolIcon pool={pool} size={18} />
      <span className="min-w-0 flex-1 truncate">{pool.name}</span>
      {pool.is_standard ? (
        <span className="shrink-0 rounded border border-border px-1.5 py-px text-[11px] font-medium text-contrast/70">
          Standard
        </span>
      ) : null}
    </>
  );

  const tenantRow = (pool: TalentPoolResponse) => {
    const checked = tenantScope && selected.has(pool.id);
    // The last pool can't be removed; past the cap, no more can be added.
    const disabled = checked
      ? selectedTenants.length <= 1
      : tenantScope && selectedTenants.length >= MAX_POOLS_PER_SEARCH;
    return (
      <label
        key={pool.id}
        className={cn(
          "flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-heading",
          disabled ? "cursor-default" : "cursor-pointer hover:bg-contrast/4",
        )}
      >
        <Checkbox
          aria-label={pool.name}
          checked={checked}
          disabled={disabled}
          onChange={(next) => toggleTenant(pool.id, next)}
        />
        {rowBody(pool)}
      </label>
    );
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          aria-label={`Search in ${selectedPools.map((p) => p.name).join(", ") || "talent pools"}`}
          className={cn("max-w-64 text-heading", className)}
        >
          {selectedPools.length ? (
            <span className="flex shrink-0 -space-x-1">
              {selectedPools.slice(0, TRIGGER_ICONS).map((pool) => (
                <PoolIcon
                  key={pool.id}
                  pool={pool}
                  size={18}
                  className="ring-2 ring-surface-primary"
                />
              ))}
            </span>
          ) : (
            <Icon name="IconPeople" size={16} className="shrink-0 opacity-50" />
          )}
          <span className="min-w-0 truncate">{label}</span>
          <Icon name="IconChevronDownSmall" size={14} className="shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-72 p-1.5">
        <div className="flex flex-col gap-0.5 px-2 pb-1.5 pt-1">
          <p className="text-sm font-medium text-heading">Search in</p>
          {tenantPools.length ? (
            <p className="text-xs text-contrast/60">
              One Arbi pool, or up to {MAX_POOLS_PER_SEARCH} of your pools.
            </p>
          ) : null}
        </div>
        {isLoading ? (
          <p className="px-2 py-1.5 text-xs text-contrast/50">Loading talent pools…</p>
        ) : isError || !data ? (
          <p className="px-2 py-1.5 text-xs text-destructive">Couldn&apos;t load talent pools.</p>
        ) : (
          <div className="flex max-h-[60vh] flex-col overflow-y-auto">
            {tenantPools.length && globalPools.length ? (
              <p className="px-2 pb-1 pt-2.5 text-xs font-medium text-contrast/50">Arbi pools</p>
            ) : null}
            {globalPools.length ? (
              <RadioGroup
                aria-label="Arbi pools"
                value={selectedGlobal?.id ?? ""}
                onValueChange={(value) => select([value as string])}
                className="flex flex-col gap-0.5"
              >
                {globalPools.map((pool) => (
                  <label
                    key={pool.id}
                    htmlFor={`${radioId}-${pool.id}`}
                    className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-heading hover:bg-contrast/4"
                  >
                    <RadioGroupItem id={`${radioId}-${pool.id}`} value={pool.id} />
                    {rowBody(pool)}
                  </label>
                ))}
              </RadioGroup>
            ) : null}
            {tenantPools.length ? (
              <>
                <p className="px-2 pb-1 pt-2.5 text-xs font-medium text-contrast/50">Your pools</p>
                {tenantPools.map(tenantRow)}
              </>
            ) : null}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
